import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Typography,
  Card,
  Alert,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Select,
  Option,
  Modal,
  ModalDialog,
  ModalClose
} from '@mui/joy';
import axiosInstance from '../util/axiosConfig';
import SizeSelect from '../components/SizeSelect';

const today = () => new Date().toISOString().slice(0, 10);

const AddItem = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(true);
  const [addVendorOpen, setAddVendorOpen] = useState(false);
  const [addVendorLoading, setAddVendorLoading] = useState(false);
  const [addVendorError, setAddVendorError] = useState('');
  const [newVendor, setNewVendor] = useState({
    name: '',
    mobile: '',
    email: '',
    address: ''
  });

  // Material form state
  const [materialForm, setMaterialForm] = useState({
    material_name: '',
    purchase_date: today(),
    quantity: '',
    rate: '',
    manufacture_date: '',
    expiry_date: '',
    vendor_id: ''
  });

  // Disposable form state
  const [disposableForm, setDisposableForm] = useState({
    disposable_name: '',
    date_entered: today(),
    quantity: '',
    rate: '',
    vendor_id: ''
  });

  // Implant form state
  const [implantForm, setImplantForm] = useState({
    implant_name: '',
    date_entered: today(),
    expiry_date: '',
    quantity: '',
    rate: '',
    size_id: '',
    vendor_id: ''
  });

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    try {
      setVendorsLoading(true);
      const response = await axiosInstance.get('/vendors');
      setVendors(response.data.vendors || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load vendors');
    } finally {
      setVendorsLoading(false);
    }
  };

  const handleMaterialChange = (e) => {
    const { name, value } = e.target || e;
    if (name === 'vendor_id' && value === '__add_new__') {
      setAddVendorOpen(true);
      return;
    }
    setMaterialForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDisposableChange = (e) => {
    const { name, value } = e.target || e;
    if (name === 'vendor_id' && value === '__add_new__') {
      setAddVendorOpen(true);
      return;
    }
    setDisposableForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImplantChange = (e) => {
    const { name, value } = e.target || e;
    if (name === 'vendor_id' && value === '__add_new__') {
      setAddVendorOpen(true);
      return;
    }
    setImplantForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNewVendorChange = (e) => {
    setNewVendor((prev) => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleAddVendor = async (e) => {
    e.preventDefault();
    setAddVendorError('');
    if (!newVendor.name.trim()) {
      setAddVendorError('Vendor name is required');
      return;
    }
    try {
      setAddVendorLoading(true);
      const response = await axiosInstance.post('/vendors', {
        name: newVendor.name.trim(),
        mobile: newVendor.mobile.trim() || undefined,
        email: newVendor.email.trim() || undefined,
        address: newVendor.address.trim() || undefined
      });
      const created = response.data.vendor;
      setVendors((prev) => [...prev, created]);
      setMaterialForm((prev) => ({ ...prev, vendor_id: String(created.id) }));
      setDisposableForm((prev) => ({ ...prev, vendor_id: String(created.id) }));
      setImplantForm((prev) => ({ ...prev, vendor_id: String(created.id) }));
      setNewVendor({ name: '', mobile: '', email: '', address: '' });
      setAddVendorOpen(false);
    } catch (err) {
      setAddVendorError(err.response?.data?.error || 'Failed to add vendor');
    } finally {
      setAddVendorLoading(false);
    }
  };

  const validateMaterialForm = () => {
    if (!materialForm.material_name || !materialForm.purchase_date ||
      !materialForm.quantity || !materialForm.vendor_id) {
      setError('All required fields must be filled');
      return false;
    }
    if (materialForm.vendor_id === '__add_new__') {
      setError('Please select a vendor or add one first');
      return false;
    }
    if (parseInt(materialForm.quantity, 10) <= 0) {
      setError('Quantity must be greater than 0');
      return false;
    }
    if (materialForm.manufacture_date && materialForm.expiry_date) {
      if (new Date(materialForm.expiry_date) <= new Date(materialForm.manufacture_date)) {
        setError('Expiry date must be later than manufacturing date');
        return false;
      }
    }
    return true;
  };

  const validateDisposableForm = () => {
    if (!disposableForm.disposable_name || !disposableForm.date_entered ||
      !disposableForm.quantity || !disposableForm.vendor_id) {
      setError('All required fields must be filled');
      return false;
    }
    if (disposableForm.vendor_id === '__add_new__') {
      setError('Please select a vendor or add one first');
      return false;
    }
    if (parseInt(disposableForm.quantity, 10) <= 0) {
      setError('Quantity must be greater than 0');
      return false;
    }
    return true;
  };

  const validateImplantForm = () => {
    if (!implantForm.implant_name?.trim() || !implantForm.date_entered ||
      !implantForm.quantity || !implantForm.size_id || !implantForm.vendor_id) {
      setError('All required fields (name, date, quantity, size, vendor) must be filled');
      return false;
    }
    if (implantForm.vendor_id === '__add_new__') {
      setError('Please select a vendor or add one first');
      return false;
    }
    if (parseInt(implantForm.quantity, 10) <= 0) {
      setError('Quantity must be greater than 0');
      return false;
    }
    return true;
  };

  const handleMaterialSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!validateMaterialForm()) return;
    try {
      setLoading(true);
      await axiosInstance.post('/materials', {
        material_name: materialForm.material_name,
        purchase_date: materialForm.purchase_date,
        quantity: parseInt(materialForm.quantity, 10),
        rate: materialForm.rate !== '' ? materialForm.rate : null,
        manufacture_date: materialForm.manufacture_date || null,
        expiry_date: materialForm.expiry_date || null,
        vendor_id: parseInt(materialForm.vendor_id, 10)
      });
      setSuccess('Material added successfully!');
      setMaterialForm({
        material_name: '',
        purchase_date: today(),
        quantity: '',
        rate: '',
        manufacture_date: '',
        expiry_date: '',
        vendor_id: ''
      });
      setTimeout(() => navigate('/stock'), 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add material');
    } finally {
      setLoading(false);
    }
  };

  const handleDisposableSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!validateDisposableForm()) return;
    try {
      setLoading(true);
      await axiosInstance.post('/disposables', {
        disposable_name: disposableForm.disposable_name,
        date_entered: disposableForm.date_entered,
        quantity: parseInt(disposableForm.quantity, 10),
        rate: disposableForm.rate !== '' ? disposableForm.rate : null,
        vendor_id: parseInt(disposableForm.vendor_id, 10)
      });
      setSuccess('Disposable added successfully!');
      setDisposableForm({
        disposable_name: '',
        date_entered: today(),
        quantity: '',
        rate: '',
        vendor_id: ''
      });
      setTimeout(() => navigate('/disposables'), 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add disposable');
    } finally {
      setLoading(false);
    }
  };

  const handleImplantSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!validateImplantForm()) return;
    try {
      setLoading(true);
      await axiosInstance.post('/implants', {
        implant_name: implantForm.implant_name,
        date_entered: implantForm.date_entered,
        expiry_date: implantForm.expiry_date || null,
        quantity: parseInt(implantForm.quantity, 10),
        rate: implantForm.rate !== '' ? implantForm.rate : null,
        size_id: implantForm.size_id || null,
        vendor_id: parseInt(implantForm.vendor_id, 10)
      });
      setSuccess('Implant added successfully!');
      setImplantForm({
        implant_name: '',
        date_entered: today(),
        expiry_date: '',
        quantity: '',
        rate: '',
        size_id: '',
        vendor_id: ''
      });
      setTimeout(() => navigate('/implants'), 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add implant');
    } finally {
      setLoading(false);
    }
  };

  const renderVendorSelect = (value, onChange, name) => (
    <FormControl sx={{ mb: 2 }}>
      <FormLabel>Vendor</FormLabel>
      <Select
        name={name}
        value={value || ''}
        onChange={(_, val) => onChange({ target: { name, value: val } })}
        placeholder={vendorsLoading ? 'Loading vendors...' : 'Select vendor'}
        disabled={vendorsLoading}
        required
        sx={{
          '&:hover': { borderColor: '#ea7a23' },
          '&.Joy-focused': { borderColor: '#ea7a23' }
        }}
      >
        {vendors.map((v) => (
          <Option key={v.id} value={String(v.id)}>
            {v.name}
            {v.mobile ? ` — ${v.mobile}` : ''}
          </Option>
        ))}
        <Option value="__add_new__">+ Add new vendor</Option>
      </Select>
    </FormControl>
  );

  return (
    <Box sx={{ padding: 3 }}>
      <Typography level="h1" sx={{ mb: 3, color: '#eb7a24' }}>
        Add Stock
      </Typography>

      {error && (
        <Alert color="danger" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert color="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Card sx={{ maxWidth: 800, mx: 'auto' }}>
        <Tabs value={activeTab} onChange={(e, value) => setActiveTab(value)}>
          <TabList>
            <Tab>Material</Tab>
            <Tab>Disposable</Tab>
            <Tab>Implant</Tab>
          </TabList>

          <TabPanel value={0}>
            <form onSubmit={handleMaterialSubmit}>
              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Material Name</FormLabel>
                <Input
                  name="material_name"
                  value={materialForm.material_name}
                  onChange={handleMaterialChange}
                  required
                  placeholder="Enter material name"
                  sx={{
                    '&:hover': { borderColor: '#ea7a23' },
                    '&:focus-within': { borderColor: '#ea7a23' }
                  }}
                />
              </FormControl>

              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Purchase Date</FormLabel>
                <Input
                  type="date"
                  name="purchase_date"
                  value={materialForm.purchase_date}
                  onChange={handleMaterialChange}
                  required
                  sx={{
                    '&:hover': { borderColor: '#ea7a23' },
                    '&:focus-within': { borderColor: '#ea7a23' }
                  }}
                />
              </FormControl>

              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Quantity</FormLabel>
                <Input
                  type="number"
                  name="quantity"
                  value={materialForm.quantity}
                  onChange={handleMaterialChange}
                  required
                  min="1"
                  placeholder="Enter quantity"
                  sx={{
                    '&:hover': { borderColor: '#ea7a23' },
                    '&:focus-within': { borderColor: '#ea7a23' }
                  }}
                />
              </FormControl>

              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Rate (per item)</FormLabel>
                <Input
                  type="number"
                  name="rate"
                  value={materialForm.rate}
                  onChange={handleMaterialChange}
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  placeholder="0.00"
                  startDecorator={<Typography sx={{ color: 'text.tertiary', fontWeight: 500 }}>₹</Typography>}
                  slotProps={{ input: { component: 'input' } }}
                  sx={{
                    '&:hover': { borderColor: '#ea7a23' },
                    '&:focus-within': { borderColor: '#ea7a23' }
                  }}
                />
              </FormControl>

              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Manufacturing Date</FormLabel>
                <Input
                  type="date"
                  name="manufacture_date"
                  value={materialForm.manufacture_date}
                  onChange={handleMaterialChange}
                  sx={{
                    '&:hover': { borderColor: '#ea7a23' },
                    '&:focus-within': { borderColor: '#ea7a23' }
                  }}
                />
              </FormControl>

              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Expiry Date</FormLabel>
                <Input
                  type="date"
                  name="expiry_date"
                  value={materialForm.expiry_date}
                  onChange={handleMaterialChange}
                  sx={{
                    '&:hover': { borderColor: '#ea7a23' },
                    '&:focus-within': { borderColor: '#ea7a23' }
                  }}
                />
              </FormControl>

              {renderVendorSelect(
                materialForm.vendor_id,
                handleMaterialChange,
                'vendor_id'
              )}

              <Button
                type="submit"
                loading={loading}
                fullWidth
                sx={{
                  backgroundColor: '#eb7a24',
                  '&:hover': { backgroundColor: '#cc6a1f' }
                }}
              >
                Add Material
              </Button>
            </form>
          </TabPanel>

          <TabPanel value={1}>
            <form onSubmit={handleDisposableSubmit}>
              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Disposable Name</FormLabel>
                <Input
                  name="disposable_name"
                  value={disposableForm.disposable_name}
                  onChange={handleDisposableChange}
                  required
                  placeholder="Enter disposable name"
                  sx={{
                    '&:hover': { borderColor: '#ea7a23' },
                    '&:focus-within': { borderColor: '#ea7a23' }
                  }}
                />
              </FormControl>

              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Purchase Date</FormLabel>
                <Input
                  type="date"
                  name="date_entered"
                  value={disposableForm.date_entered}
                  onChange={handleDisposableChange}
                  required
                  sx={{
                    '&:hover': { borderColor: '#ea7a23' },
                    '&:focus-within': { borderColor: '#ea7a23' }
                  }}
                />
              </FormControl>

              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Quantity</FormLabel>
                <Input
                  type="number"
                  name="quantity"
                  value={disposableForm.quantity}
                  onChange={handleDisposableChange}
                  required
                  min="1"
                  placeholder="Enter quantity"
                  sx={{
                    '&:hover': { borderColor: '#ea7a23' },
                    '&:focus-within': { borderColor: '#ea7a23' }
                  }}
                />
              </FormControl>

              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Rate (per item)</FormLabel>
                <Input
                  type="number"
                  name="rate"
                  value={disposableForm.rate}
                  onChange={handleDisposableChange}
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  placeholder="0.00"
                  startDecorator={<Typography sx={{ color: 'text.tertiary', fontWeight: 500 }}>₹</Typography>}
                  slotProps={{ input: { component: 'input' } }}
                  sx={{
                    '&:hover': { borderColor: '#ea7a23' },
                    '&:focus-within': { borderColor: '#ea7a23' }
                  }}
                />
              </FormControl>

              {renderVendorSelect(
                disposableForm.vendor_id,
                handleDisposableChange,
                'vendor_id'
              )}

              <Button
                type="submit"
                loading={loading}
                fullWidth
                sx={{
                  backgroundColor: '#eb7a24',
                  '&:hover': { backgroundColor: '#cc6a1f' }
                }}
              >
                Add Disposable
              </Button>
            </form>
          </TabPanel>

          <TabPanel value={2}>
            <form onSubmit={handleImplantSubmit}>
              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Implant Name</FormLabel>
                <Input
                  name="implant_name"
                  value={implantForm.implant_name}
                  onChange={handleImplantChange}
                  required
                  placeholder="Enter implant name"
                  sx={{
                    '&:hover': { borderColor: '#ea7a23' },
                    '&:focus-within': { borderColor: '#ea7a23' }
                  }}
                />
              </FormControl>

              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Purchase Date</FormLabel>
                <Input
                  type="date"
                  name="date_entered"
                  value={implantForm.date_entered}
                  onChange={handleImplantChange}
                  required
                  sx={{
                    '&:hover': { borderColor: '#ea7a23' },
                    '&:focus-within': { borderColor: '#ea7a23' }
                  }}
                />
              </FormControl>

              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Expiry Date</FormLabel>
                <Input
                  type="date"
                  name="expiry_date"
                  value={implantForm.expiry_date}
                  onChange={handleImplantChange}
                  sx={{
                    '&:hover': { borderColor: '#ea7a23' },
                    '&:focus-within': { borderColor: '#ea7a23' }
                  }}
                />
              </FormControl>

              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Quantity</FormLabel>
                <Input
                  type="number"
                  name="quantity"
                  value={implantForm.quantity}
                  onChange={handleImplantChange}
                  required
                  min="1"
                  placeholder="Enter quantity"
                  sx={{
                    '&:hover': { borderColor: '#ea7a23' },
                    '&:focus-within': { borderColor: '#ea7a23' }
                  }}
                />
              </FormControl>

              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Rate (per item)</FormLabel>
                <Input
                  type="number"
                  name="rate"
                  value={implantForm.rate}
                  onChange={handleImplantChange}
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  placeholder="0.00"
                  startDecorator={<Typography sx={{ color: 'text.tertiary', fontWeight: 500 }}>₹</Typography>}
                  slotProps={{ input: { component: 'input' } }}
                  sx={{
                    '&:hover': { borderColor: '#ea7a23' },
                    '&:focus-within': { borderColor: '#ea7a23' }
                  }}
                />
              </FormControl>

              <SizeSelect
                value={implantForm.size_id}
                onChange={(val) => setImplantForm((p) => ({ ...p, size_id: val }))}
                label="Size"
                required
              />

              {renderVendorSelect(
                implantForm.vendor_id,
                handleImplantChange,
                'vendor_id'
              )}

              <Button
                type="submit"
                loading={loading}
                fullWidth
                sx={{
                  backgroundColor: '#eb7a24',
                  '&:hover': { backgroundColor: '#cc6a1f' }
                }}
              >
                Add Implant
              </Button>
            </form>
          </TabPanel>
        </Tabs>
      </Card>

      <Modal open={addVendorOpen} onClose={() => setAddVendorOpen(false)}>
        <ModalDialog sx={{ maxWidth: 400 }}>
          <ModalClose />
          <Typography level="h4" sx={{ mb: 2, color: '#eb7a24' }}>
            Add Vendor
          </Typography>
          {addVendorError && (
            <Alert color="danger" sx={{ mb: 2 }} onClose={() => setAddVendorError('')}>
              {addVendorError}
            </Alert>
          )}
          <form onSubmit={handleAddVendor}>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Name</FormLabel>
              <Input
                name="name"
                value={newVendor.name}
                onChange={handleNewVendorChange}
                required
                placeholder="Vendor name"
                sx={{
                  '&:hover': { borderColor: '#ea7a23' },
                  '&:focus-within': { borderColor: '#ea7a23' }
                }}
              />
            </FormControl>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Mobile</FormLabel>
              <Input
                name="mobile"
                value={newVendor.mobile}
                onChange={handleNewVendorChange}
                placeholder="Mobile number"
                sx={{
                  '&:hover': { borderColor: '#ea7a23' },
                  '&:focus-within': { borderColor: '#ea7a23' }
                }}
              />
            </FormControl>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Email</FormLabel>
              <Input
                name="email"
                type="email"
                value={newVendor.email}
                onChange={handleNewVendorChange}
                placeholder="Email"
                sx={{
                  '&:hover': { borderColor: '#ea7a23' },
                  '&:focus-within': { borderColor: '#ea7a23' }
                }}
              />
            </FormControl>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Address</FormLabel>
              <Input
                name="address"
                value={newVendor.address}
                onChange={handleNewVendorChange}
                placeholder="Address"
                sx={{
                  '&:hover': { borderColor: '#ea7a23' },
                  '&:focus-within': { borderColor: '#ea7a23' }
                }}
              />
            </FormControl>
            <Button
              type="submit"
              loading={addVendorLoading}
              fullWidth
              sx={{
                backgroundColor: '#eb7a24',
                '&:hover': { backgroundColor: '#cc6a1f' }
              }}
            >
              Add Vendor
            </Button>
          </form>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default AddItem;
